import React from "react";
import {
    Box,
    Container,
    Flex,
    Heading,
    Text,
    Stack,
    List,
    chakra, VStack,
} from "@chakra-ui/react";
import FadeInSection from "../components/animation/FadeInSection";
import SectionCard from "../components/SectionCard";
import Highlight from "../components/Highlight";

const AboutPage = () => {
    return (
        <Box>

            <SectionCard>
                <Box flex="2" textAlign="center">
                    <chakra.img
                        src="./Joerg_Talk.jpg"
                        alt="Jörg Schultz Portrait"
                        maxW="300px"
                        borderRadius="md"
                        mx="auto"
                    />
                </Box>
                <VStack flex="3" align="start" spacing={4}>
                    <Heading as="h2" size="lg">
                        About Me
                    </Heading>
                    <Text fontSize="md">
                        Hi, I’m Jörg — a <Highlight>computational biologist</Highlight> with a deep love for both
                        structured thinking and the
                        unpredictable world of animal behavior. I spent over a two decades in academia, leading research at
                        the crossroads of
                        biology, mathematics, and computer science. But at some point, I felt the pull toward projects
                        with more immediate,
                        tangible impact.
                    </Text>
                    <Text fontSize="md">
                        Outside the lab, I’m also an <Highlight>ambitious amateur in dog training</Highlight> — and
                        that's where my two worlds started
                        to blend. To bring this work onto a professional footing, I co-founded <Highlight>Tier Wohl
                        Team</Highlight>, where I now develop
                        software, AI systems, and connected tools that make professional training easier, smarter, and
                        more data-driven.
                        Whether it's wearable sensors, mobile apps, or modular AI agents, my goal is to support trainers
                        in the field —
                        so they can focus on what matters most: working with the dog.
                    </Text>
                </VStack>
            </SectionCard>

            <SectionCard flexDirection="row-reverse">
                <Box flex="2" textAlign="center">
                    <chakra.img
                        src="./AT101_Graduate_2.jpg" // Replace with your actual image
                        alt="Dog training presentation"
                        maxW="300px"
                        mx="auto"
                    />
                </Box>
                <VStack flex="3" align="start" spacing={4}>
                    <Heading as="h2" size="lg">
                        Practical Experience — Grounded in Real-World Training
                    </Heading>
                    <Text fontSize="md">
                        While I come from a science and tech background, I've also spent years immersing myself in the
                        world of dog training.
                        As an <Highlight>ambitious amateur</Highlight>, I’ve combined formal education with hands-on
                        experience to build a deep understanding of canine behavior —
                        both in structured training environments and out in the field.
                    </Text>

                    <List.Root spacing={2} pl={4}>
                        <List.Item>
                            General Behavior Modification Certification from{" "}
                            <chakra.a
                                href="https://animaltraining.us/general-behavior-modification-certification/"
                                target="_blank"
                                rel="noopener noreferrer"
                                color="brand.green.500"
                                _hover={{ textDecoration: "underline", color: "brand.green.600" }}
                            >
                                Animal Training & Research International Center
                            </chakra.a>{" "}
                            — including full course, written exam, and live animal assessment.
                        </List.Item>
                        <List.Item>
                            <chakra.a
                                href="https://www.trainingsspezialist.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                color="brand.green.500"
                                _hover={{ textDecoration: "underline", color: "brand.green.600" }}
                            >
                                Trainingsspezialist Hund
                            </chakra.a>{" "}
                            — a comprehensive, 12-module program in modern, science-based training strategies.
                        </List.Item>
                        <List.Item>
                            Chicken Camp Modules 1–4 at{" "}
                            <chakra.a
                                href="https://steigerwald-t.de/de/huehnerseminare-die-ausbildung-fuer-tiertrainer-und-tierbesitzer/"
                                target="_blank"
                                rel="noopener noreferrer"
                                color="brand.green.500"
                                _hover={{ textDecoration: "underline", color: "brand.green.600" }}
                            >
                                Steigerwald.T
                            </chakra.a>{" "}
                            — practical, hands-on training focused on timing, shaping, and precision.
                        </List.Item>
                        <List.Item>
                            Co-Trainer at the <chakra.span fontStyle="italic">adolescent dog group</chakra.span> for four years at the{" "}
                            <chakra.a
                                href="https://www.wuerzburger-hundeschule.de/"
                                target="_blank"
                                rel="noopener noreferrer"
                                color="brand.green.500"
                                _hover={{ textDecoration: "underline", color: "brand.green.600" }}
                            >
                                Würzburg Animal Shelter’s dog training school
                            </chakra.a>
                            , gaining hands-on experience in group-based training for young dogs.
                        </List.Item>
                    </List.Root>

                    <Text fontSize="md">
                        These are just a few of the programs and experiences that shape my perspective.
                        They provide the foundation for the technology I build —
                        ensuring it supports real-world dog training, not just abstract ideas.
                    </Text>
                </VStack>
            </SectionCard>

            <SectionCard>
                <Box flex="2" textAlign="center">
                    <chakra.img
                        src="./HumanGenome.jpg"
                        alt="Scientific Work"
                        maxW="250px"
                        mx="auto"
                        borderRadius="md"
                    />
                </Box>
                <VStack flex="3" align="start" spacing={4}>
                    <Heading as="h2" size="lg">
                        Computational Biology Meets Canine Training
                    </Heading>
                    <Text fontSize="md">
                        While my focus today is on supporting animal trainers with intelligent tools,
                        my roots lie in a different data-rich domain: <Highlight>computational biology</Highlight>.
                    </Text>
                    <Text fontSize="md">
                        For over two decades, I worked as a <Highlight>tenured professor</Highlight>,
                        researching genomics and transcriptomics — fields built on massive, complex datasets.
                        I led interdisciplinary projects, taught students, and supervised researchers, all while building
                        custom software tools to analyze and visualize biological data. In fact, I began developing
                        <Highlight>web-based applications</Highlight> as early as 1997 — long before it was cool.
                    </Text>
                    <Text fontSize="md">
                        Throughout my academic career, I authored more than 100 peer-reviewed publications.
                        You can find a complete list of them on my{" "}
                        <chakra.a
                            href="https://www.researchgate.net/profile/Joerg-Schultz-2/publications"
                            target="_blank"
                            rel="noopener noreferrer"
                            textDecoration="underline"
                            color="brand.green.600"
                            _hover={{ color: "brand.green.700" }}
                        >
                            ResearchGate profile
                        </chakra.a>.
                    </Text>
                    <Text fontSize="md">
                        This scientific background still shapes my work today. It gave me a deep respect for{" "}
                        <Highlight>data, structure, and reproducibility</Highlight> — values I now bring into
                        the world of animal training. Whether I’m building an AI agent or processing sensor signals,
                        the goal is the same: to translate complexity into clarity, and give trainers the tools they
                        need to make confident, informed decisions.
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                        You can also download my{" "}
                        <chakra.a
                            href="/JoergSchultz_CV.pdf"
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            textDecoration="underline"
                            color="brand.green.600"
                            _hover={{ color: "brand.green.700" }}
                        >
                            professional CV (PDF)
                        </chakra.a>{" "}
                        for a complete overview.
                    </Text>
                </VStack>
            </SectionCard>
            <SectionCard flexDirection="row-reverse">
                <Box flex="1" textAlign="center">
                    <chakra.img
                        src="./TWT_Logo.png"
                        alt="Tier Wohl Team Logo"
                        maxW="300px"
                        mx="auto"
                    />
                </Box>
                <Box flex="2">
                    <Heading as="h2" size="xl" mb={4}>
                        Tier Wohl Team – Philosophy and Values
                    </Heading>
                    <Stack spacing={4}>
                        <Text fontSize="md">
                            At the core of our work lies a deep respect for animals and a commitment to their well-being.
                            We believe that technology should serve a purpose — to enhance the lives of dogs and their human partners.
                        </Text>
                        <Text fontSize="md">
                            We create tools that are not only innovative, but also compassionate, reliable, and grounded in real-world needs.
                            Whether it’s intelligent training plans, behavior tracking, or wearable sensors, every solution we build is
                            designed to support both the physical and emotional welfare of animals.
                        </Text>
                        <Text fontSize="md">
                            As we grow, we remain committed to quality, integrity, and responsible innovation — because the trust of animals
                            and their people is not something we take lightly.
                        </Text>
                        <Text fontSize="md" color="gray.600">
                            Learn more about our company at{" "}
                            <chakra.a
                                href="https://www.tier-wohl-team.de/en-gb/tools"
                                target="_blank"
                                rel="noopener noreferrer"
                                textDecoration="underline"
                            >
                                tier-wohl-team.de
                            </chakra.a>
                        </Text>
                    </Stack>
                </Box>
            </SectionCard>
        </Box>
    );
};

export default AboutPage;
